import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useMemo, useCallback, useEffect, useRef} from 'react'
import type {IdToPullRequestMap} from '../types/pull'
import type {HydratedTask, OptimisticTask, TaskType, UITask} from '../types/task'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {
  mergeServerTasksWithOptimistic,
  syncSingleTaskCachesFromList,
  transformAPITasksToUnhydrated,
} from '../utils/task-helpers'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {usePullRequests} from './usePullRequests'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {useDebounce} from '@github-ui/use-debounce'
import {tasksApiPathWithParams, type TasksQueryParams} from '../utils/api-url-helpers'
import type {APITask} from '../types/api-task'
import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {useRepoInfo} from './use-repo-info'
import type {TaskList, TaskListPage} from './repo-task-utils'
import {parseLinkHeader} from '../utils/pagination-helpers'
import {ALL_REPOSITORIES_FILTER, NO_REPO_FILTER} from './use-repo-tasks-filter'

import {getInteractiveRefetchInterval, hasInteractiveTasks} from '../agents/interactive-polling'
import {createPersister} from '@github-ui/react-query-persister'
import type {Persister} from '@github-ui/react-query-persister/types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {useHiddenEarlyTaskIds} from '../ahp/early-task-visibility'

export const agentTasksQueryKey = ['agent-tasks']
export const PAGE_SIZE = 10

const TaskListPageValidator = Compile(
  Type.Object(
    {
      tasks: Type.Array(
        Type.Object(
          {id: Type.Optional(Type.String()), latest_session_updated_at: Type.String()},
          {additionalProperties: true},
        ),
      ),
      hasNextPage: Type.Boolean(),
      counts: Type.Optional(Type.Object({open: Type.Number(), closed: Type.Number()}, {additionalProperties: true})),
    },
    {additionalProperties: true},
  ),
)

// Optimistic tasks only live until the next poll, so a persisted page containing one is already out of date.
const noOptimisticTasksValidator = {
  name: 'agent-tasks-no-optimistic',
  isValid: (data: unknown) => !(data as TaskListPage).tasks.some(task => task.isOptimistic === true),
}

/**
 * IndexedDB persister for a `/agents/tasks` list page. Restored pages are shown
 * immediately and revalidated in the background once they are older than the query's `staleTime`.
 */
export function createTaskListPersister({
  sendAnalyticsEvent,
  enabled = true,
  partition,
}: {
  sendAnalyticsEvent: SendAnalyticsEventFunction
  enabled?: boolean
  /** Keeps entries saved in different contexts, such as under different SSO access, apart. */
  partition?: string
}): Persister {
  return createPersister({
    validator: TaskListPageValidator,
    dataValidator: noOptimisticTasksValidator,
    sendAnalyticsEvent,
    prefix: partition === undefined ? 'agent-sessions-tasks' : `agent-sessions-tasks-${partition}`,
    maxAgeMs: 1000 * 60 * 60 * 24,
    alwaysRefetch: false,
    enabled,
  })
}

export type TaskFilter = 'active' | 'completed' | 'all'

/**
 * Get the query key for agent tasks with pagination parameters.
 *
 * **Note:** pageSize is intentionally NOT included in the query key so that all views
 * (regardless of their pageSize setting) share the same cache. This allows optimistic
 * updates from task creation to appear in all views.
 *
 * `scope` opts a consumer out of that shared cache (e.g. the sidebar uses its own
 * scope so its growing pageSize doesn't fight the content list's fixed-size poll).
 * When omitted, the key is identical to the unscoped key and continues to share
 * the cache with other unscoped callers.
 * @param page - Page number
 * @param taskFilter - Which tasks to fetch: 'active', 'completed', or 'all'
 *
 */
export function getAgentTasksQueryKey({
  page,
  taskFilter,
  taskType,
  repository,
  repoId,
  customAgentName,
  statuses,
  sort,
  query,
  agentIds,
  includeEnvironmentKinds,
  creatorIds,
  scope,
}: {
  page: number
  taskFilter: TaskFilter
  taskType?: TaskType
  repository?: string
  repoId?: number
  customAgentName?: string
  statuses?: string[]
  sort?: string
  query?: string
  agentIds?: number[]
  includeEnvironmentKinds?: readonly EnvironmentKind[]
  creatorIds?: number[]
  scope?: string
}): readonly unknown[] {
  const base = [
    ...agentTasksQueryKey,
    page,
    taskFilter,
    {
      taskType,
      repository,
      repoId,
      customAgentName,
      statuses,
      sort,
      query,
      agentIds,
      includeEnvironmentKinds,
      creatorIds,
    },
  ]
  return scope === undefined ? base : [...base, scope]
}

/**
 * Get the query key for agent tasks to be used for mutations to ensure optimistic tasks are always added to the correct view.
 */
export function getAgentTasksQueryKeyForMutation(): readonly unknown[] {
  return getAgentTasksQueryKey({page: 1, taskFilter: 'active'})
}

/**
 * Returns a callback that invalidates every agent-tasks list query (all scopes and
 * filters), forcing an immediate refetch instead of waiting for the next poll.
 */
export function useInvalidateAgentTasks(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
  }, [queryClient])
}

export type TaskCounts = {
  open: number
  closed: number
  needsAttention?: number
}

export const fetchTasks = async ({
  queryClient,
  copilotApiUrl,
  queryParams,
  queryKey,
}: {
  queryClient: QueryClient
  copilotApiUrl?: string | undefined
  queryParams?: TasksQueryParams
  queryKey?: readonly unknown[]
}): Promise<TaskListPage> => {
  const path = tasksApiPathWithParams(queryParams)

  const response = await fetchCAPI({
    path,
    copilotApiUrl,
  })
  const data = await response.json()

  const apiTasks = data.tasks as APITask[]
  const serverTasks = transformAPITasksToUnhydrated(apiTasks)

  // Get existing tasks from cache so that we can find and preserve optimistic tasks until next poll
  // Use the provided queryKey to read from the correct cache (includes page/archived params)
  const existingTasks = queryClient.getQueryData<TaskListPage>(queryKey ?? agentTasksQueryKey)?.tasks || []

  // Merge server tasks with existing optimistic tasks
  const optimisticTasks = existingTasks.filter(task => task.isOptimistic === true) as OptimisticTask[]
  const mergedTasks = mergeServerTasksWithOptimistic(serverTasks, optimisticTasks)

  // Extract pagination and counts from response, using Link header for pagination if available
  const hasNextPage = parseLinkHeader(response.headers?.get('Link')) ?? !!data.has_next_page

  let taskCounts: TaskCounts | undefined
  if (typeof data.total_active_count === 'number' && typeof data.total_archived_count === 'number') {
    taskCounts = {
      open: data.total_active_count,
      closed: data.total_archived_count,
    }
    if (typeof data.total_waiting_for_user_count === 'number') {
      taskCounts.needsAttention = data.total_waiting_for_user_count
    }
  }

  return {tasks: mergedTasks, hasNextPage, counts: taskCounts}
}

/**
 * Convert TaskFilter to the API's archived parameter.
 * 'active' -> false, 'completed' -> true, 'all' -> undefined
 */
function taskFilterToArchived(taskFilter: TaskFilter): boolean | undefined {
  if (taskFilter === 'all') return undefined
  return taskFilter === 'completed'
}

/**
 * Parameters shared by {@link useTasksCore} and {@link useTasks}.
 */
export type UseTasksCoreParams = {
  sessionsPollingInterval?: number | false
  initialTasks?: HydratedTask[]
  taskFilter?: TaskFilter
  includeCounts?: boolean
  currentPage?: number
  pageSize?: number
  taskType?: TaskType
  /** Optional repo filter: a NWO (e.g., "owner/repo") to filter by repo, "none" for repo-less tasks, or undefined for all. */
  repoFilter?: string
  /** Optional numeric repository ID to scope tasks to a specific repo via the global endpoint */
  repoId?: number
  /** Optional custom agent name to filter tasks by exact match */
  customAgentName?: string
  /** Optional creator IDs to filter tasks by */
  creatorIds?: number[]
  /** Optional detailed statuses to filter by */
  statuses?: string[]
  /** Optional sort direction: 'newest' (default) or 'oldest' */
  sort?: string
  /** Optional free-text search query */
  searchQuery?: string
  /** Optional agent IDs to filter tasks by */
  agentIds?: number[]
  /** Environment kinds to include alongside sessions matching agent IDs. */
  includeEnvironmentKinds?: readonly EnvironmentKind[]
  enabled?: boolean
  /** Optional cache scope. Consumers that pass a scope get a distinct cache entry
   * from unscoped consumers (which all share one entry per filter combination).
   * Use for views whose pageSize diverges from the default content list (e.g. the sidebar). */
  scope?: string
  /** Optional persister for the task list query, e.g. from {@link createTaskListPersister}. Pair it with a `scope`
   * so a persisted entry is never shared with consumers that did not opt in. */
  persister?: Persister
  /** How long fetched tasks stay fresh before a mount or window focus refetches them. Defaults to the client default. */
  staleTime?: number
  /**
   * Poll while mounted even when no interactive sessions are listed, at the same interval used for interactive
   * sessions. Use it where changes that don't send an Alive event, such as task renames, must still show up.
   */
  alwaysPoll?: boolean
  /** Preserve hidden rows for client-side pagination; callers must filter them after slicing. */
  preserveHiddenTaskPositions?: boolean
}

/**
 * Core task fetching: subscribes to alive updates, runs the `/agents/tasks` query
 * (including counts when requested), and keeps single-task caches in sync — WITHOUT
 * any PR/repo hydration.
 *
 * Use this directly when you only need raw tasks or counts (e.g. a badge count) and
 * want to avoid the extra PR/repo network requests that {@link useTasks} performs.
 */
export function useTasksCore({
  sessionsPollingInterval = 3000,
  initialTasks,
  taskFilter = 'active',
  includeCounts,
  currentPage = 1,
  pageSize = PAGE_SIZE,
  taskType,
  repoFilter,
  repoId,
  customAgentName,
  creatorIds,
  statuses,
  sort,
  searchQuery,
  agentIds,
  includeEnvironmentKinds,
  enabled = true,
  scope,
  persister,
  staleTime,
  alwaysPoll = false,
  preserveHiddenTaskPositions = false,
}: UseTasksCoreParams = {}) {
  const queryClient = useQueryClient()

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: agentTasksQueryKey,
    })
    queryClient.invalidateQueries({
      queryKey: ['agent-sessions-pull-requests'],
    })
  }, [queryClient])

  // When sessionsPollingInterval is false, disable alive subscription entirely (used for cache warm-up scenarios)
  const shouldSubscribeToAlive = sessionsPollingInterval !== false

  const debouncedHandleAliveUpdate = useDebounce(
    handleAliveUpdate,
    typeof sessionsPollingInterval === 'number' ? sessionsPollingInterval : 3000,
    {maxWait: 10000},
  )

  // Subscribe to alive updates (disabled when sessionsPollingInterval is false)
  // enableTaskUpdates ensures the task list refetches when a task state changes
  // (e.g., CLI session resumes → terminal → non-terminal), so the sidebar picks
  // up the new state and useCliTaskPolling can start polling for fresh stale_at.
  useAgentSessionsAlive({
    onEvent: shouldSubscribeToAlive ? debouncedHandleAliveUpdate : () => {},
    enableCreateEvents: false,
    enableUpdateEvents: shouldSubscribeToAlive,
    enableLogUpdates: false,
    enableTaskUpdates: shouldSubscribeToAlive,
    enabled: shouldSubscribeToAlive,
  })

  // Always use the global /agents/tasks endpoint. When a repo is selected,
  // pass repository_id to scope results. The repo-scoped endpoint is only used
  // by the repo-agents page (separate hook).
  const isNoRepoFilter = repoFilter === NO_REPO_FILTER
  const isAllRepositoriesFilter = repoFilter === ALL_REPOSITORIES_FILTER

  const queryParams: TasksQueryParams = {
    archived: taskFilterToArchived(taskFilter),
    creatorIds,
    includeCounts,
    page: currentPage,
    pageSize,
    type: taskType,
    withRepo: isNoRepoFilter ? false : isAllRepositoriesFilter ? true : undefined,
    customAgentName,
    statuses,
    sort,
    query: searchQuery,
    agentIds,
    includeEnvironmentKinds,
    repositoryId: repoId,
  }

  // Include all filter dimensions in query key for proper cache separation
  const queryKey = getAgentTasksQueryKey({
    page: currentPage,
    taskFilter,
    taskType,
    repository: repoFilter,
    repoId,
    customAgentName,
    statuses,
    sort,
    query: searchQuery,
    agentIds,
    includeEnvironmentKinds,
    creatorIds,
    scope,
  })
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useCAPIQuery<TaskListPage>({
    queryKey,
    queryFn: async () =>
      await fetchTasks({
        queryClient,
        queryParams,
        queryKey,
      }),
    initialData: initialTasks ? {tasks: initialTasks as TaskListPage['tasks'], hasNextPage: false} : undefined,
    refetchInterval: ({state: {data}}) =>
      getInteractiveRefetchInterval(alwaysPoll || hasInteractiveTasks(data?.tasks ?? [])),
    // The Remote CLI behavior is best suited being as close to real-time as possible,
    // so fetching fresh data on refocus helps.
    refetchOnWindowFocus: true,
    enabled,
    ...(persister ? {persister} : {}),
    ...(staleTime === undefined ? {} : {staleTime}),
  })

  // Refetch when pageSize changes since it's not in the query key
  // This ensures we fetch enough items when the user increases the page size
  const prevPageSizeRef = useRef(pageSize)
  useEffect(() => {
    if (prevPageSizeRef.current !== pageSize) {
      prevPageSizeRef.current = pageSize
      query.refetch()
    }
  }, [pageSize, query])

  // Get all tasks from the query result (already includes optimistic tasks)
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const allTasks = useMemo(
    () =>
      query.data?.tasks.filter(task => preserveHiddenTaskPositions || !task.id || !hiddenTaskIds.has(task.id)) ?? [],
    [query.data, hiddenTaskIds, preserveHiddenTaskPositions],
  )
  const hasNextPage = query.data?.hasNextPage ?? false
  const counts = query.data?.counts

  // Propagate fresh `stale_at` values into any open single-task caches so the
  // detail view picks up updates from the list without waiting for its own poll.
  useEffect(() => {
    syncSingleTaskCachesFromList(queryClient, allTasks)
  }, [allTasks, queryClient])

  return {query, allTasks, hasNextPage, counts}
}

/**
 * Repository IDs whose info {@link useTasks} waits on before showing tasks: every non-optimistic
 * task with a repository, except tasks whose pull request has already loaded.
 */
export function getRepoIdsNeedingFetch(tasks: TaskList, pullRequestsById: IdToPullRequestMap = {}): number[] {
  const ids: number[] = []
  for (const task of tasks) {
    if (task.isOptimistic) continue
    if (task.resource_id && pullRequestsById[task.resource_id]) continue // Has PR, don't need to fetch repo info
    if (task.repo_id) {
      ids.push(task.repo_id)
    }
  }
  return ids
}

// TODO: Combine this with the use-repo-tasks hook once we are fully on the task API to avoid duplication
export function useTasks({
  initialPullRequestsMap,
  repoInfoPersister,
  ...coreParams
}: UseTasksCoreParams & {
  /** Optional initial PR map for hydration on hard navigation */
  initialPullRequestsMap?: IdToPullRequestMap
  /** Optional persister for the repository info that hydrates tasks without pull requests. */
  repoInfoPersister?: Persister
} = {}) {
  const {query, allTasks, hasNextPage, counts} = useTasksCore(coreParams)

  // Get pull requests for tasks that have valid, non-optimistic resource IDs
  const tasksWithResourceIds = allTasks.filter(task => task.resource_id !== undefined && task.resource_id !== -1)
  const {data: idToPullRequestMap, isError: isErrorPullRequests} = usePullRequests({
    tasks: tasksWithResourceIds,
    initialPullRequestsMap,
  })

  // Get repo IDs for tasks without pull requests
  const repoIdsNeedingFetch = useMemo(
    // Use initialPullRequestsMap when available (hard nav) for parallel loading with PR fetching
    () => getRepoIdsNeedingFetch(allTasks, initialPullRequestsMap ?? idToPullRequestMap),
    [allTasks, initialPullRequestsMap, idToPullRequestMap],
  )

  // Fetch repo info for tasks without pull requests
  const {
    data: repoMap,
    isLoading: isLoadingRepoInfo,
    isError: isErrorRepoInfo,
  } = useRepoInfo(repoIdsNeedingFetch, {persister: repoInfoPersister})

  // Attach pull request data to tasks where available, but only return hydrated or optimistic tasks
  // Memoize to prevent unnecessary re-renders of task list items when data hasn't changed
  const returnTasks = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- .map().filter() widens the union; assertion keeps downstream types correct
    return allTasks
      .map(task => {
        const pull = task.resource_id ? idToPullRequestMap[task.resource_id] : undefined

        if (pull) {
          return {
            ...task,
            isOptimistic: false, // Be sure to always mark any transitional tasks that have pull requests as no longer optimistic
            pull,
            nwo: pull.repository_nwo,
          }
        } else {
          // If no pull request is found, return the task without pull data but include repo nwo
          const repo = task.repo_id ? repoMap[task.repo_id] : undefined
          return {
            ...task,
            pull: undefined,
            nwo: repo ? `${repo.ownerLogin}/${repo.name}` : task.nwo,
            repositoryName: repo?.name ?? task.repositoryName,
          }
        }
      })
      .filter(task => {
        // Always include optimistic tasks
        if (task.isOptimistic === true) {
          return true
        }

        // Include tasks with nwo (tasks without PRs yet), tasks with PR data,
        // or repo-less tasks (e.g. CLI sessions with no repository)
        return task.pull !== undefined || task.nwo !== undefined || (task.id && !task.repo_id)
      }) as UITask[]
  }, [allTasks, idToPullRequestMap, repoMap])

  const hasTasks = allTasks.length > 0
  const isLoadingInitialTasks = query.isLoading && !query.data
  const isLoadingRepoData = isLoadingRepoInfo && repoIdsNeedingFetch.length > 0 && hasTasks

  // PR data loads in the background and hydrates tasks as it arrives
  const isLoading = isLoadingInitialTasks || isLoadingRepoData

  const isError = query.isError || isErrorPullRequests || isErrorRepoInfo

  const isFetching = query.isFetching

  return {
    tasks: returnTasks,
    dataUpdatedAt: query.dataUpdatedAt,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    counts,
  }
}
